import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { $crud } from "../helper/CrudFactory";
import { callAPI } from "../helper/apiUtils";
import LanguageContext from "../helper/LanguageContext";
import { SettingsBanner, SettingsNote, SettingsRow, SettingsSection, SettingsTag } from "./settings/SettingsList";
import "./EmailForwardSettings.css";

/**
 * Kjuup forwarding address (Kjuup.io): for users who do not want to connect a mailbox.
 * Kjuup creates a personal address; mail forwarded there goes into rooms, tasks and the
 * daily report like synced mail. BE: GET/POST /email/forward-address, …/regenerate,
 * removal via DELETE /email/accounts/:id.
 */
export default function EmailForwardSettings({ strings = {}, locked = false }) {
  const { isGerm } = React.useContext(LanguageContext);
  const tr = (key, de, en) => strings[key] || (isGerm ? de : en);
  const subscription = useSelector((s) => s.subscription) || {};
  const isKjuupPlusMember = !!subscription.isKjuupPlusMember;
  const emailAccountsChangedAt = useSelector((s) => s.emailAccountsChangedAt);
  const [account, setAccount] = useState(null);
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [openGuide, setOpenGuide] = useState(null);

  const fetchAddress = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await callAPI("/email/forward-address", {}, "GET");
      if (data?.status) {
        setAccount(data.account || null);
        setConfigured(data.configured !== false);
      }
    } catch (e) {
      console.error("forward address fetch failed", e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (locked) return;
    fetchAddress();
  }, [fetchAddress, locked]);

  // New mail, a Gmail confirmation or a change on another device (`emailAccount` socket).
  useEffect(() => {
    if (locked || !emailAccountsChangedAt) return;
    fetchAddress({ silent: true });
  }, [emailAccountsChangedAt, fetchAddress, locked]);

  const copy = async (text, message) => {
    try {
      await navigator.clipboard.writeText(text);
      $crud.notify({ type: "success", message });
    } catch (_) {
      $crud.notify({ type: "error", message: tr("email_forward_copy_failed", "Kopieren nicht möglich", "Could not copy") });
    }
  };

  const create = async () => {
    if (busy) return;
    setBusy("create");
    try {
      const { data } = await callAPI("/email/forward-address", {}, "POST", {});
      if (data?.code === "forward_inbox_not_configured") {
        setConfigured(false);
        return;
      }
      if (!data?.status) {
        $crud.notify({ type: "error", message: data?.message || tr("email_forward_create_failed", "Adresse konnte nicht angelegt werden", "Could not create the address") });
        return;
      }
      setAccount(data.account);
      $crud.notify({ type: "success", message: tr("email_forward_created", "Weiterleitungsadresse angelegt", "Forwarding address created") });
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async () => {
    try {
      await $crud.confirm({
        title: tr("email_forward_regenerate", "Neue Adresse erzeugen", "Generate new address"),
        textContent: tr(
          "email_forward_regenerate_confirm",
          "E-Mails an die bisherige Adresse kommen danach nicht mehr in Kjuup an. Passe deine Weiterleitung anschließend an. Bereits empfangene E-Mails bleiben erhalten.",
          "Emails sent to the current address will no longer reach Kjuup. Update your forwarding afterwards. Emails already received are kept."
        ),
        options: {
          ok: tr("email_forward_regenerate", "Neue Adresse erzeugen", "Generate new address"),
          cancel: strings.cancel || (isGerm ? "Abbrechen" : "Cancel"),
        },
      });
    } catch (_) {
      return;
    }
    setBusy("regenerate");
    try {
      const { data } = await callAPI("/email/forward-address/regenerate", {}, "POST", {});
      if (!data?.status) {
        $crud.notify({ type: "error", message: data?.message || tr("email_forward_create_failed", "Adresse konnte nicht angelegt werden", "Could not create the address") });
        return;
      }
      setAccount(data.account);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    try {
      await $crud.confirm({
        title: tr("email_forward_delete", "Weiterleitungsadresse löschen", "Delete forwarding address"),
        textContent: tr(
          "email_forward_delete_confirm",
          "Die Adresse wird deaktiviert und alle darüber empfangenen E-Mails werden aus Kjuup entfernt.",
          "The address is deactivated and all emails received through it are removed from Kjuup."
        ),
        options: {
          ok: tr("email_forward_delete_ok", "Löschen", "Delete"),
          cancel: strings.cancel || (isGerm ? "Abbrechen" : "Cancel"),
        },
      });
    } catch (_) {
      return;
    }
    setBusy("delete");
    try {
      const { data } = await callAPI(`/email/accounts/${account.id}`, {}, "DELETE");
      if (data?.status === false) {
        $crud.notify({ type: "error", message: data.message });
        return;
      }
      setAccount(null);
    } finally {
      setBusy(null);
    }
  };

  const formatWhen = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString(isGerm ? "de-DE" : "en-GB", { dateStyle: "medium", timeStyle: "short" });
  };

  const guides = [
    {
      key: "gmail",
      icon: "mail",
      label: "Gmail",
      steps: [
        tr("email_forward_gmail_1", "Gmail im Browser öffnen → Zahnrad → „Alle Einstellungen aufrufen“ → Tab „Weiterleitung und POP/IMAP“.", "Open Gmail in the browser → gear icon → “See all settings” → tab “Forwarding and POP/IMAP”."),
        tr("email_forward_gmail_2", "„Weiterleitungsadresse hinzufügen“ wählen und die Kjuup-Adresse einfügen.", "Choose “Add a forwarding address” and paste the Kjuup address."),
        tr("email_forward_gmail_3", "Gmail schickt einen Bestätigungscode an Kjuup. Er erscheint nach etwa einer Minute oben auf dieser Seite.", "Gmail sends a confirmation code to Kjuup. It appears at the top of this page after about a minute."),
        tr("email_forward_gmail_4", "Code in Gmail eingeben, „Kopie des eingehenden Mails weiterleiten an“ aktivieren und „Gmail-Kopie im Posteingang behalten“ wählen. Speichern.", "Enter the code in Gmail, turn on “Forward a copy of incoming mail to” and choose “keep Gmail's copy in the Inbox”. Save."),
      ],
    },
    {
      key: "outlook",
      icon: "mail",
      label: "Outlook / Microsoft 365",
      steps: [
        tr("email_forward_outlook_1", "Outlook im Web öffnen → Einstellungen → E-Mail → „Weiterleitung“.", "Open Outlook on the web → Settings → Mail → “Forwarding”."),
        tr("email_forward_outlook_2", "„Weiterleitung aktivieren“ einschalten, die Kjuup-Adresse eintragen und „Kopie weitergeleiteter Nachrichten beibehalten“ aktivieren. Speichern.", "Turn on “Enable forwarding”, enter the Kjuup address and tick “Keep a copy of forwarded messages”. Save."),
        tr("email_forward_outlook_3", "Nur bestimmte E-Mails? Unter „Regeln“ eine Regel anlegen, z. B. für einzelne Absender oder Projekte, mit der Aktion „Weiterleiten an“.", "Only some emails? Create a rule under “Rules”, e.g. for certain senders or projects, with the action “Forward to”."),
        tr("email_forward_outlook_4", "Bei Firmenkonten kann die IT externe Weiterleitungen sperren – dann bitte dort freigeben lassen.", "For company accounts, IT may block external forwarding – ask them to allow it."),
      ],
    },
    {
      key: "other",
      icon: "alternate_email",
      label: tr("email_forward_other", "Andere Anbieter & einzelne E-Mails", "Other providers & single emails"),
      steps: [
        tr("email_forward_other_1", "GMX, WEB.DE, IONOS, T-Online & Co.: in den Postfach-Einstellungen unter „Weiterleitung“ oder „Filterregeln“ die Kjuup-Adresse eintragen.", "GMX, WEB.DE, IONOS, T-Online etc.: enter the Kjuup address in the mailbox settings under “Forwarding” or “Filter rules”."),
        tr("email_forward_other_2", "Einzelne E-Mails kannst du jederzeit von Hand an die Adresse weiterleiten. Kjuup erkennt dabei den ursprünglichen Absender.", "You can forward single emails to the address by hand at any time. Kjuup detects the original sender."),
      ],
    },
  ];

  const confirmation = account?.gmailConfirmation;
  const lastReceived = formatWhen(account?.lastReceivedAt);

  return (
    <div className="email-forward-settings">
      <SettingsNote className="email-forward-settings__intro">
        {tr(
          "email_forward_info",
          "Du möchtest kein Postfach mit Kjuup verbinden? Kjuup legt dir eine eigene E-Mail-Adresse an. Leite deine E-Mails automatisch oder einzeln dorthin weiter – sie werden deinen Projekten zugeordnet, erzeugen Aufgaben und fließen in deinen täglichen Report ein.",
          "Don't want to connect a mailbox to Kjuup? Kjuup creates an email address for you. Forward your emails to it, automatically or one by one – they are assigned to your projects, create tasks and go into your daily report."
        )}
      </SettingsNote>

      {locked ? (
        <SettingsBanner
          tone="warn"
          icon="lock"
          title={isKjuupPlusMember
            ? tr("kjuup_io_company_managed_body_short", "Bitte deinen Firmen-Admin, dir im Admin Panel einen Kjuup.io-Platz zuzuweisen.", "Ask your company admin to assign you a Kjuup.io seat in the Admin Panel.")
            : tr("kjuup_io_trial_ended_body", "Abonniere Kjuup.io, um E-Mail und weitere Premium-Funktionen weiter zu nutzen.", "Subscribe to Kjuup.io to keep using email and other premium features.")}
        />
      ) : loading && !account ? (
        <div className="kj-settings-card email-forward-settings__skeleton" aria-busy="true">
          <span className="email-forward-settings__bone" />
        </div>
      ) : !account ? (
        configured ? (
          <SettingsSection>
            <SettingsRow
              icon="forward_to_inbox"
              label={tr("email_forward_create", "Weiterleitungsadresse anlegen", "Create forwarding address")}
              busy={busy === "create"}
              onClick={busy ? undefined : create}
            />
          </SettingsSection>
        ) : (
          <SettingsBanner
            tone="warn"
            icon="schedule"
            title={tr("email_forward_unavailable", "Die Weiterleitungsadresse ist gerade nicht verfügbar. Bitte versuche es später erneut.", "The forwarding address is not available right now. Please try again later.")}
          />
        )
      ) : (
        <>
          {confirmation ? (
            <SettingsBanner
              tone="warn"
              icon="mark_email_unread"
              title={tr("email_forward_gmail_pending", "Gmail wartet auf deine Bestätigung", "Gmail is waiting for your confirmation")}
              body={`${tr("email_forward_gmail_code", "Bestätigungscode", "Confirmation code")}: ${confirmation.code || "–"}${confirmation.forwardingFrom ? ` · ${confirmation.forwardingFrom}` : ""}`}
            >
              {confirmation.code ? (
                <button
                  type="button"
                  className="kj-btn kj-btn--primary kj-btn--sm"
                  onClick={() => copy(confirmation.code, tr("email_forward_code_copied", "Code kopiert", "Code copied"))}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">content_copy</span>
                  <span>{tr("email_forward_copy_code", "Code kopieren", "Copy code")}</span>
                </button>
              ) : null}
              {confirmation.link ? (
                <a
                  className="kj-btn kj-btn--outline kj-btn--sm"
                  href={confirmation.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">open_in_new</span>
                  <span>{tr("email_forward_confirm_link", "Weiterleitung bestätigen", "Confirm forwarding")}</span>
                </a>
              ) : null}
            </SettingsBanner>
          ) : null}

          <SettingsSection
            caption={tr("email_forward_address", "Deine Weiterleitungsadresse", "Your forwarding address")}
            note={tr(
              "email_forward_secret_note",
              "Jede E-Mail an diese Adresse landet in deinem Kjuup. Gib sie nicht weiter – bei Bedarf erzeugst du eine neue.",
              "Every email sent to this address ends up in your Kjuup. Keep it to yourself – you can generate a new one at any time."
            )}
          >
            <SettingsRow
              icon="forward_to_inbox"
              label={<span className="email-forward-settings__address">{account.email}</span>}
              sub={lastReceived
                ? `${tr("email_forward_last_received", "Zuletzt empfangen", "Last received")}: ${lastReceived} · ${account.receivedCount}`
                : tr("email_forward_nothing_yet", "Noch keine E-Mail empfangen", "No email received yet")}
              trailing={(
                <>
                  {lastReceived ? <SettingsTag tone="ok">{tr("email_forward_active", "Aktiv", "Active")}</SettingsTag> : null}
                  <span className="material-symbols-outlined email-forward-settings__copy" aria-hidden="true">content_copy</span>
                </>
              )}
              title={tr("email_forward_copy_address", "Adresse kopieren", "Copy address")}
              chevron={false}
              onClick={() => copy(account.email, tr("email_forward_address_copied", "Adresse kopiert", "Address copied"))}
            />
          </SettingsSection>

          <SettingsSection caption={tr("email_forward_setup", "Weiterleitung einrichten", "Set up forwarding")}>
            {guides.map((guide) => (
              <SettingsRow
                key={guide.key}
                icon={guide.icon}
                label={guide.label}
                expanded={openGuide === guide.key}
                onClick={() => setOpenGuide((cur) => (cur === guide.key ? null : guide.key))}
              >
                <ol className="email-forward-settings__steps">
                  {guide.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </SettingsRow>
            ))}
          </SettingsSection>

          <SettingsSection>
            <SettingsRow
              icon="autorenew"
              label={tr("email_forward_regenerate", "Neue Adresse erzeugen", "Generate new address")}
              busy={busy === "regenerate"}
              chevron={false}
              onClick={busy ? undefined : regenerate}
            />
            <SettingsRow
              icon="delete"
              danger
              label={tr("email_forward_delete", "Weiterleitungsadresse löschen", "Delete forwarding address")}
              busy={busy === "delete"}
              onClick={busy ? undefined : remove}
            />
          </SettingsSection>
        </>
      )}
    </div>
  );
}
